import FileCopyIcon from "@mui/icons-material/FileCopy";
import {
  Button,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  IconButton,
  Theme,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import { useTheme } from "@mui/material/styles";
import { DatePicker } from "@mui/x-date-pickers";
import ChangeReasonDialog from "components/ChangeReasonDialog";
import EntityDetails from "components/EntityDetails";
import RedirectBackButton from "components/RedirectBackButton";
import RowElement from "components/RowElement";
import SnackbarMessage from "components/SnackbarMessage";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useFormattedTitleFromUser from "hooks/useFormattedTitleFromUser";
import { useEffect, useMemo, useState } from "react";
import { Field, withTypes } from "react-final-form";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import {
  ModifyEnrollmentRequest,
  OrganizationUserDetails,
  ResponseCode,
  ResponseOfSkus,
  ResponseOfUserSkuExtended,
} from "services/KnowfullyAdminClient";
import { Severity, SnackbarMessageProps } from "types/common-types";
import {
  convertToUTC,
  convertToZonedDate,
  dateToStandardFormat,
  standardDateFormat,
} from "utils/dateUtils";

const useStyles = (theme: Theme) => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
  copyButton: {
    marginLeft: "10px",
  },
  entityDetails: {
    mb: theme.spacing(2),
  },
  certifyCourse: {
    margin: "30px 0",
  },
  confirmationMessageBlock: {
    margin: "15px 0",
  },
  confirmationMessageLine1: {
    marginBottom: "10px",
  },
});

type ModifyFormValues = Omit<
  ModifyEnrollmentRequest,
  "userSkuId" | "init" | "toJSON"
>;

const { Form } = withTypes<ModifyFormValues>();

const ModifyEnrollmentPage = () => {
  const { userId, userSkuId } = useParams<{
    userId: string;
    userSkuId: string;
  }>();
  const styles = useStyles(useTheme());
  const { formatMessage: t } = useIntl();
  const queryClient = useQueryClient();
  const [formValues, setFormValues] = useState<ModifyFormValues>();
  const [changeReason, setChangeReason] = useState("");
  const [changeReasonDialogOpen, setChangeReasonDialogOpen] = useState(false);

  const {
    organizationUserService,
    userSkuService,
    skuService,
    userCourseUrl,
  } = useGlobalContext();

  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const copyToClipboard = (value: string) => {
    navigator.clipboard.writeText(value);
    setSnackbarMessage(
      new SnackbarMessageProps(
        t({ id: "copiedToClipboardMessage" }),
        Severity.Success
      )
    );
  };

  const [enrollmentModified, enrollmentModifiedActions] = useBoolean(false);

  const userResult = useQuery<OrganizationUserDetails>(
    ["getOrganizationUserDetails", userId],
    () => organizationUserService.getOrganizationUserDetails(userId!),
    {
      enabled: !!userId,
    }
  );

  const user = userResult?.data;
  useFormattedTitleFromUser(
    "viewModifyRegistrationTitle",
    user,
    t({ id: "user" })
  );

  const userSkuResult = useQuery<ResponseOfUserSkuExtended>(
    ["getUserSku", userSkuId],
    () =>
      userSkuService.getUserSku(
        user?.id,
        user?.organizationId,
        userSkuId!
      ),
    { enabled: !!userResult && !userResult.isLoading }
  );

  const userSku = userSkuResult?.data?.data!;

  const skuResult = useQuery<ResponseOfSkus>(
    ["sku", userSku?.skuId],
    () => skuService.getSkuById(userSku?.skuId!),
    {
      enabled: !!userSku && !userSkuResult.isLoading,
    }
  );

  const sku = skuResult?.data?.data!;

  useEffect(() => {
    if (enrollmentModified) {
      setSnackbarMessage(
        new SnackbarMessageProps(
          t({ id: "enrollmentChangesSaved" }),
          Severity.Success
        )
      );

      enrollmentModifiedActions.setFalse();
    }
  }, [enrollmentModified, enrollmentModifiedActions, t]);

  const loading =
    userResult.isLoading || userSkuResult.isLoading || skuResult.isLoading;

  const rows = [
    RowElement(
      "userNameLabel",
      `${user?.firstName ?? ""} ${user?.lastName ?? ""}`,
      loading
    ),
    RowElement("emailLabel", user?.email!, loading),
    RowElement(
      "userId",
      user?.id!,
      loading,
      <>
        <span>{user?.id ?? "-"}</span>
        <IconButton
          sx={styles.copyButton}
          size="small"
          onClick={() => user?.id && copyToClipboard(user.id)}
        >
          <FileCopyIcon />
        </IconButton>
      </>
    ),
    RowElement("courseName", userSku?.title ?? "", loading),
    RowElement(
      "skuLabel",
      sku?.sku ?? "",
      loading,
      <>
        <span>{sku?.sku ?? "-"}</span>
        <IconButton
          sx={styles.copyButton}
          size="small"
          onClick={() => sku?.sku && copyToClipboard(sku.sku)}
        >
          <FileCopyIcon />
        </IconButton>
      </>
    ),
    RowElement(
      "userSkuId",
      userSkuId!,
      loading,
      <>
        <span>{userSkuId}</span>
        <IconButton
          sx={styles.copyButton}
          size="small"
          onClick={() => userSkuId && copyToClipboard(userSkuId)}
        >
          <FileCopyIcon />
        </IconButton>
      </>
    ),
  ].filter((r) => !!r.component);

  const init = useMemo<ModifyEnrollmentRequest>(() => {
    let startDate = convertToZonedDate(userSku?.startDate!);
    let endDate = convertToZonedDate(userSku?.endDate!);

    return ModifyEnrollmentRequest.fromJS({
      userSkuId,
      startDate,
      endDate,
    });
  }, [userSku, userSkuId]);

  if (userResult.isError || userSkuResult.isError || skuResult.isError) {
    return (
      <Alert severity="error">
        <pre>{t({ id: "fetchError" })}</pre>
      </Alert>
    );
  }

  const modifyEnrollment = async () => {
    if (!formValues) return;
    let startDate = convertToUTC(formValues.startDate);
    let endDate = convertToUTC(formValues.endDate);

    const result = await userSkuService.modifyEnrollment(
      ModifyEnrollmentRequest.fromJS({
        ...formValues,
        userId: user?.id,
        organizationId: user?.organizationId,
        userSkuId,
        startDate,
        endDate,
        changeReason,
      })
    );

    if (result.responseCode === ResponseCode.OK) {
      setSnackbarMessage(
        new SnackbarMessageProps(t({ id: "enrollmentChangesSaved" }))
      );

      queryClient.invalidateQueries(["getUserSku", userSkuId]);
      setChangeReasonDialogOpen(false);
    }
  };

  return (
    <Container>
      <RedirectBackButton
        redirectRoute={userCourseUrl(userId, userSkuId)}
        labelKey="backToUserCourse"
      />

      <h1>{t({ id: "viewModifyRegistration" })}</h1>

      <EntityDetails
        details={rows}
        title={t({ id: "registrationDetails" })}
        sx={styles.entityDetails}
      />

      <Form
        onSubmit={(values) => {
          setFormValues(values);
          setChangeReasonDialogOpen(true);
        }}
        initialValues={init}
        render={({ handleSubmit, submitting, submitError, values }) => {
          const saveButtonEnabled =
            !userSkuResult.isFetching &&
            !userSkuResult.isLoading &&
            values.startDate &&
            values.endDate &&
            (!init.startDate ||
              dateToStandardFormat(values.startDate) !==
                dateToStandardFormat(init.startDate) ||
              !init.endDate ||
              dateToStandardFormat(values.endDate) !==
                dateToStandardFormat(init.endDate));

          return (
            <form onSubmit={handleSubmit} noValidate>
              <Grid container spacing={2} alignItems="center">
                <Grid item>
                  <Field name="startDate">
                    {({ input, meta }) => (
                      <FormControl
                        fullWidth
                        error={meta.error && meta.submitFailed}
                      >
                        <DatePicker
                          disabled={!userSkuResult.isFetched}
                          label={t({ id: "startDateLabel" })}
                          format={standardDateFormat}
                          slotProps={{
                            textField: {
                              variant: "standard",
                              error: meta.error && meta.submitFailed,
                              helperText:
                                meta.error && meta.submitFailed && meta.error,
                            },
                            actionBar: {
                              actions: ["clear", "cancel"],
                            },
                          }}
                          {...input}
                          value={input.value === "" ? null : input.value}
                          data-cy="skuStartDatePicker"
                        />
                      </FormControl>
                    )}
                  </Field>
                </Grid>
                <Grid item>
                  <Field name="endDate">
                    {({ input, meta }) => (
                      <FormControl
                        fullWidth
                        error={meta.error && meta.submitFailed}
                      >
                        <DatePicker
                          disabled={!userSkuResult.isFetched}
                          label={t({ id: "endDateLabel" })}
                          format={standardDateFormat}
                          slotProps={{
                            textField: {
                              variant: "standard",
                              error: meta.error && meta.submitFailed,
                              helperText:
                                meta.error && meta.submitFailed && meta.error,
                            },
                            actionBar: {
                              actions: ["clear", "cancel"],
                            },
                          }}
                          {...input}
                          value={input.value === "" ? null : input.value}
                          data-cy="skuEndDatePicker"
                        />
                      </FormControl>
                    )}
                  </Field>
                </Grid>
                <Grid item>
                  <Button
                    variant="contained"
                    type="submit"
                    color="primary"
                    disabled={submitting || !saveButtonEnabled}
                    data-cy="submitButton"
                  >
                    {submitting && (
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                    )}
                    <T id="saveChangesLabel" />
                  </Button>
                </Grid>
              </Grid>
              {submitError && (
                <Alert sx={styles.feedback} severity="error">
                  {submitError}
                </Alert>
              )}
            </form>
          );
        }}
      />
      <ChangeReasonDialog
        changeReason={changeReason}
        setChangeReason={setChangeReason}
        open={changeReasonDialogOpen}
        onCancel={() => setChangeReasonDialogOpen(false)}
        handleConfirm={modifyEnrollment}
      />
      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Container>
  );
};

export default ModifyEnrollmentPage;
